import { useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { EntityType } from '../../types';
import { RelationChip, ActionBtn } from './EntityModalComponents';
import { ContactPicker } from './ContactPicker';
import type { EntityModalSidebarProps } from './entity-modal.types';

const RELATION_LABELS: Record<string, string> = {
  related_to: 'common.link_to',
  depends_on: 'common.depends_on',
  mentions: 'common.mentions',
  same_topic: 'common.same_subject',
  prerequisite: 'common.prerequisite',
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  task: 'common.type_task',
  goal: 'common.type_goal',
  note: 'common.type_note',
  event: 'common.type_event',
  contact: 'common.type_contact',
};

function SidebarGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
        {title}
      </div>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

export function EntityModalSidebar({
  entityType,
  entityId,
  formData,
  linkedGoal,
  linkedEvent,
  linkedTask,
  subTasks,
  relatedTasks,
  relatedNotes,
  relatedEvents,
  semanticRelations,
  onNavigate,
  onCreateLinked,
  onUnlink,
  entityContactLinks,
  linkedContacts,
  allContacts,
  onLinkContact,
  onUnlinkContact,
  onQuickCreateContact,
  onAdvancedCreateContact,
  reverseEntityLinks,
  subGoals,
  goalEvents,
  indirectGoal,
}: EntityModalSidebarProps) {
  const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState<{ top: number; right: number } | null>(null);
  const contactPickerBtnRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  return (
    <div className="w-[230px] border-l border-zinc-700/25 p-4 flex flex-col gap-5 shrink-0 overflow-y-auto">
      {/* Forward Links (Owned) */}
      {(linkedGoal || linkedEvent || linkedTask) && (
        <SidebarGroup title={t('entity.sidebar_relations')}>
          {linkedGoal && (
            <RelationChip
              label={linkedGoal.title}
              entityType="goal"
              onClick={() => onNavigate('goal', linkedGoal.id)}
              onUnlink={() =>
                onUnlink(
                  { stopPropagation: () => {} } as React.MouseEvent,
                  entityType,
                  entityId,
                  'goalId',
                  true
                )
              }
            />
          )}
          {linkedEvent && (
            <RelationChip
              label={linkedEvent.title}
              entityType="event"
              onClick={() => onNavigate('event', linkedEvent.id)}
              onUnlink={() =>
                onUnlink(
                  { stopPropagation: () => {} } as React.MouseEvent,
                  entityType,
                  entityId,
                  'eventId',
                  true
                )
              }
            />
          )}
          {linkedTask && (
            <RelationChip
              label={linkedTask.title}
              entityType="task"
              onClick={() => onNavigate('task', linkedTask.id)}
              onUnlink={() =>
                onUnlink(
                  { stopPropagation: () => {} } as React.MouseEvent,
                  entityType,
                  entityId,
                  entityType === 'task' ? 'parentId' : 'taskId',
                  true
                )
              }
            />
          )}
        </SidebarGroup>
      )}

      {/* Indirect Goal (Task: task → event → goal) */}
      {entityType === 'task' && indirectGoal && (
        <SidebarGroup title={t('entity.sidebar_indirect_goal')}>
          <div className="text-[10px] text-zinc-500 mb-1">
            {t('entity.sidebar_via_event', { title: indirectGoal.viaEvent.title })}
          </div>
          <RelationChip
            label={indirectGoal.goal.title}
            entityType="goal"
            onClick={() => onNavigate('goal', indirectGoal.goal.id)}
          />
        </SidebarGroup>
      )}

      {/* Sub-tasks */}
      {subTasks.length > 0 && (
        <SidebarGroup title={t('entity.sidebar_subtasks')}>
          {subTasks.map((st) => (
            <RelationChip
              key={st.id}
              label={st.title}
              entityType="task"
              strikethrough={st.status === 'done'}
              onClick={() => onNavigate('task', st.id)}
              onUnlink={() =>
                onUnlink(
                  { stopPropagation: () => {} } as React.MouseEvent,
                  'task',
                  st.id,
                  'parentId',
                  false
                )
              }
            />
          ))}
        </SidebarGroup>
      )}

      {/* Related Tasks (Goal/Event) */}
      {relatedTasks.length > 0 && (
        <SidebarGroup title={t('entity.sidebar_linked_tasks')}>
          {relatedTasks.map((rt) => (
            <RelationChip
              key={rt.id}
              label={rt.title}
              entityType="task"
              strikethrough={rt.status === 'done'}
              onClick={() => onNavigate('task', rt.id)}
              onUnlink={() =>
                onUnlink(
                  { stopPropagation: () => {} } as React.MouseEvent,
                  'task',
                  rt.id,
                  entityType === 'goal' ? 'goalId' : 'eventId',
                  false
                )
              }
            />
          ))}
        </SidebarGroup>
      )}

      {/* Sub-goals (Goal modal) */}
      {entityType === 'goal' && subGoals && subGoals.length > 0 && (
        <SidebarGroup title={t('entity.sidebar_sub_goals')}>
          {subGoals.map((g) => (
            <RelationChip
              key={g.id}
              label={g.title}
              entityType="goal"
              onClick={() => onNavigate('goal', g.id)}
            />
          ))}
        </SidebarGroup>
      )}

      {/* Events linked to goal (Goal modal) — with sub-tasks */}
      {entityType === 'goal' && goalEvents && goalEvents.length > 0 && (
        <SidebarGroup title={t('entity.sidebar_linked_events')}>
          {goalEvents.map((e) => (
            <div key={e.id}>
              <RelationChip
                label={e.title}
                entityType="event"
                onClick={() => onNavigate('event', e.id)}
              />
              {e.tasks.length > 0 && (
                <div className="ml-5 mt-1 flex flex-col gap-1">
                  {e.tasks.map((et) => (
                    <RelationChip
                      key={et.id}
                      label={et.title}
                      entityType="task"
                      strikethrough={et.status === 'done'}
                      onClick={() => onNavigate('task', et.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </SidebarGroup>
      )}

      {/* Related Notes */}
      {relatedNotes.length > 0 && (
        <SidebarGroup title={t('entity.sidebar_linked_notes')}>
          {relatedNotes.map((n) => (
            <RelationChip
              key={n.id}
              label={n.title}
              entityType="note"
              onClick={() => onNavigate('note', n.id)}
              onUnlink={() =>
                onUnlink(
                  { stopPropagation: () => {} } as React.MouseEvent,
                  'note',
                  n.id,
                  entityType === 'task' ? 'taskId' : entityType === 'goal' ? 'goalId' : 'eventId',
                  false
                )
              }
            />
          ))}
        </SidebarGroup>
      )}

      {/* Related Events (Contact) */}
      {entityType === 'contact' && relatedEvents.length > 0 && (
        <SidebarGroup title={t('entity.sidebar_linked_events')}>
          {relatedEvents.map((e) => (
            <RelationChip
              key={e.id}
              label={e.title}
              entityType="event"
              onClick={() => onNavigate('event', e.id)}
            />
          ))}
        </SidebarGroup>
      )}

      {/* Universal Contacts (all types except contact) */}
      {entityType !== 'contact' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              {t('entity.sidebar_linked_contacts')}
            </div>
            <button
              ref={contactPickerBtnRef}
              type="button"
              onClick={() => {
                if (!isContactPickerOpen) {
                  const rect = contactPickerBtnRef.current?.getBoundingClientRect();
                  if (rect) {
                    setPickerPosition({
                      top: rect.bottom + 4,
                      right: window.innerWidth - rect.right,
                    });
                  }
                }
                setIsContactPickerOpen(!isContactPickerOpen);
              }}
              className="p-1 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded transition-colors cursor-pointer"
              title={t('entity.sidebar_add_contact')}
            >
              <Plus size={14} />
            </button>
          </div>

          {isContactPickerOpen && pickerPosition && (
            <ContactPicker
              linkedContactIds={entityContactLinks.map((l) => l.contactId)}
              allContacts={allContacts}
              position={pickerPosition}
              onSelectExisting={(contactId) => {
                onLinkContact(contactId);
                setIsContactPickerOpen(false);
              }}
              onCreateQuick={(name, role) => {
                onQuickCreateContact(name, role);
                setIsContactPickerOpen(false);
              }}
              onCreateAdvanced={() => {
                onAdvancedCreateContact();
                setIsContactPickerOpen(false);
              }}
              onClose={() => setIsContactPickerOpen(false)}
            />
          )}

          {linkedContacts.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              {linkedContacts.map((c) => (
                <RelationChip
                  key={c.id}
                  label={c.name}
                  entityType="contact"
                  onClick={() => onNavigate('contact', c.id)}
                  onUnlink={() => onUnlinkContact(c.id)}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-zinc-600 italic">{t('entity.sidebar_no_contact')}</p>
          )}
        </div>
      )}

      {/* Reverse lookup: entities linked to this contact */}
      {entityType === 'contact' && reverseEntityLinks && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
            {t('entity.sidebar_linked_entities')}
          </div>
          {reverseEntityLinks.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              {reverseEntityLinks.map((link) => (
                <RelationChip
                  key={`${link.entityType}-${link.entityId}`}
                  label={link.label}
                  entityType={link.entityType}
                  onClick={() => onNavigate(link.entityType as EntityType, link.entityId)}
                  onUnlink={() => onUnlinkContact(link.entityId)}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-zinc-600 italic">{t('entity.sidebar_no_entity')}</p>
          )}
        </div>
      )}

      {/* VB-030 — Semantic Relations */}
      {semanticRelations.length > 0 && (
        <div className="pt-4 border-t border-zinc-700/30">
          <SidebarGroup title={t('entity.sidebar_semantic_links')}>
            {semanticRelations.map((rel) => {
              const isSource = rel.source_id === entityId;
              const otherType = isSource ? rel.target_type : rel.source_type;
              const otherId = isSource ? rel.target_id : rel.source_id;
              const entityTypeLabel = t(ENTITY_TYPE_LABELS[otherType] ?? otherType);
              const relationLabel = t(RELATION_LABELS[rel.relation_type] ?? rel.relation_type);
              return (
                <RelationChip
                  key={rel.id}
                  label={
                    ((rel as Record<string, unknown>)._resolvedLabel as string) || entityTypeLabel
                  }
                  entityType={otherType}
                  subtitle={`${entityTypeLabel} · ${relationLabel}`}
                  onClick={() => onNavigate(otherType as EntityType, otherId)}
                />
              );
            })}
          </SidebarGroup>
        </div>
      )}

      {/* Contextual Actions */}
      {['task', 'goal', 'event', 'contact'].includes(entityType) && (
        <div className="mt-auto pt-4 border-t border-zinc-700/30">
          <SidebarGroup title={t('entity.sidebar_actions')}>
            {['task', 'goal', 'event'].includes(entityType) && (
              <ActionBtn
                label={t('entity.sidebar_write_note')}
                color="#22c55e"
                onClick={() =>
                  onCreateLinked(
                    'note',
                    entityType === 'task'
                      ? { taskId: entityId, domainId: formData.domainId }
                      : entityType === 'goal'
                        ? { goalId: entityId, domainId: formData.domainId }
                        : { eventId: entityId, domainId: formData.domainId }
                  )
                }
              />
            )}
            {['task', 'goal', 'contact'].includes(entityType) && (
              <ActionBtn
                label={t('entity.sidebar_plan_event')}
                color="#f59e0b"
                onClick={() =>
                  onCreateLinked('event', {
                    title: t('entity.sidebar_event_prefix', {
                      title: formData.title || formData.name,
                    }),
                    domainId: formData.domainId,
                    ...(entityType === 'contact' && { contactIds: [entityId] }),
                  })
                }
              />
            )}
            {['goal', 'event'].includes(entityType) && (
              <ActionBtn
                label={t('entity.sidebar_create_linked_task')}
                color="#3b82f6"
                onClick={() =>
                  onCreateLinked(
                    'task',
                    entityType === 'goal'
                      ? { goalId: entityId, domainId: formData.domainId }
                      : { eventId: entityId, domainId: formData.domainId }
                  )
                }
              />
            )}
            {entityType === 'task' && (
              <ActionBtn
                label={t('entity.sidebar_create_subtask')}
                color="#3b82f6"
                onClick={() =>
                  onCreateLinked('task', { parentId: entityId, domainId: formData.domainId })
                }
              />
            )}
          </SidebarGroup>
        </div>
      )}
    </div>
  );
}
